import { useTranslations } from 'next-intl';
import Heading from '@atoms/Heading/Heading';
import Text from '@atoms/Text/Text';
import Link from '@atoms/Link/Link';
import Table from '@atoms/Table/Table';
import {
  StyledEnergyDeclarationImage,
  StyledGhsLabels,
  StyledGhsSymbols,
  StyledGhsWrapper,
  StyledInfoBlock,
  StyledGhsIcon,
} from './ProductTableOfOther.styles';
import type {
  AxfoodProductDetailsViewModel,
  BatteryInformationData,
  FeedingData,
  FeedingDetailsData,
  FeedingFrequencyData,
} from '@occ/api-client';
import config from '@config';
import Contact from './Contact';

const GHS_SIGNAL_WORDS = ['danger', 'not_applicable', 'warning'] as const;

type GhsSignalWord = (typeof GHS_SIGNAL_WORDS)[number];

type Props = {
  data: AxfoodProductDetailsViewModel;
};

const ProductTableOfOther = ({ data }: Props) => {
  const t = useTranslations('product');

  const ghsSignalWordKeys: Record<GhsSignalWord, `${GhsSignalWord}`> = {
    danger: 'danger',
    not_applicable: 'not_applicable',
    warning: 'warning',
  };
  const precautionaryStatements = data.hazards?.length
    ? data.hazards.concat(data.precautionaryStatements)
    : data.precautionaryStatements;

  const renderInfoBlock = (title: string, details: string[]) => {
    return (
      <StyledInfoBlock>
        <Heading variant="h3" label>
          {title}
        </Heading>

        {details &&
          details?.map((detail) => (
            <Text key={detail.replaceAll(' ', '')} type="body">
              {detail}
            </Text>
          ))}
      </StyledInfoBlock>
    );
  };

  const getFeedingDetailString = (feedingDetails: FeedingDetailsData[] = [], feedingFrequencyId = 0) =>
    feedingDetails
      .find((detail) => detail.feedingFrequencyId === feedingFrequencyId)
      ?.amount?.map((unit) => unit)
      .join(', ');

  return (
    <>
      {data.labels && data.labels.length > 0 && (
        <StyledInfoBlock>
          <StyledGhsWrapper>
            <StyledGhsSymbols>
              {data.labels.map(
                (item: string, index) =>
                  config.GHS_SYMBOLS.includes(item) && (
                    <StyledGhsIcon key={`${item}-${index}`} src={`/icons/ghs/${item}.svg`} />
                  )
              )}
            </StyledGhsSymbols>
            <StyledGhsLabels>
              {data.labels.map(
                (item: string, index) =>
                  GHS_SIGNAL_WORDS.includes(item as GhsSignalWord) && (
                    <Text key={`${item}-${index}`} type="label">
                      {t(ghsSignalWordKeys[item as GhsSignalWord])}
                    </Text>
                  )
              )}
            </StyledGhsLabels>
          </StyledGhsWrapper>
        </StyledInfoBlock>
      )}

      {precautionaryStatements?.length > 0 && renderInfoBlock(t('precautionaryStatement'), precautionaryStatements)}

      {data.dietTypeInformation && renderInfoBlock(t('dietTypeInformation'), [data.dietTypeInformation])}

      {data.consumerStorageInstructions &&
        renderInfoBlock(t('consumerStorageInstructions'), [data.consumerStorageInstructions])}

      {data.consumerUsageInstructions &&
        renderInfoBlock(t('consumerUsageInstructions'), [data.consumerUsageInstructions])}

      {data.preparationDescription && renderInfoBlock(t('preparationDescription'), [data.preparationDescription])}

      {data.maxStorageTemperature && renderInfoBlock(t('maxStorageTemprature'), [data.maxStorageTemperature])}

      {data.minStorageTemperature && renderInfoBlock(t('minStorageTemprature'), [data.minStorageTemperature])}

      {(data.areBatteriesIncluded || data.areBatteriesRequired || data.batteryInformation?.length > 0) && (
        <StyledInfoBlock>
          <Heading variant="h3" label>
            {t('batteryInformation')}
          </Heading>
          {data.areBatteriesIncluded !== null && (
            <Text type="body">
              {`${t('areBatteriesIncluded')} ${data.areBatteriesIncluded ? t('batteryTrue') : t('batteryFalse')}`}
            </Text>
          )}
          {data.areBatteriesRequired !== null && (
            <Text type="body">
              {`${t('areBatteriesRequired')} ${data.areBatteriesRequired ? t('batteryTrue') : t('batteryFalse')}`}
            </Text>
          )}

          {data.batteryInformation?.length > 0 &&
            data.batteryInformation.map((item: BatteryInformationData, index) => (
              <Text key={`${item}-${index}`} type="body">
                {`${item.batteriesQuantityRequired || ''} ${item.batteryType || ''} ${
                  item.batteryTechnologyTypeCode || ''
                } ${item.batteryWeight || ''} ${item.batteryQuantityBuiltIn || ''} ${
                  item.batteryQuantityBuiltIn ? t('batteryQuantityBuiltIn') : ''
                }`}
              </Text>
            ))}
        </StyledInfoBlock>
      )}

      {/*{data.isDrugProduct && renderInfoBlock(t('drugInformation'), [data.drugInformation])}*/}

      {/*{data.isDrugProduct && renderInfoBlock(t('drugIllumination'), [data.drugIllumination])}*/}

      <Contact contacts={data.contacts} name={data.foodContactName} address={data.foodContactAddress} />

      {data.regulatoryAct && renderInfoBlock(t('regulatoryAct'), [data.regulatoryAct])}

      {data.compulsoryAdditiveLabelInformation &&
        renderInfoBlock(t('compulsoryAdditiveLabelInformation'), [data.compulsoryAdditiveLabelInformation])}

      {data.code && renderInfoBlock(t('articleNumber'), [data.code])}

      {data.animalData && (data.animalData?.feedingInstructions || (data.animalData.feedingData?.length ?? 0) > 0) && (
        <StyledInfoBlock>
          {data.animalData?.feedingInstructions && (
            <div>
              <Heading variant="h3" label>
                {t('feedingInstructions')}
              </Heading>
              <Text type="body">{data.animalData?.feedingInstructions}</Text>
            </div>
          )}

          {data.animalData?.feedingFrequencies && data.animalData?.feedingData && (
            <StyledInfoBlock>
              <Table>
                <thead>
                  <tr>
                    <th>{t('animalWeight')}</th>
                    <th>{t('animalAge')}</th>
                    {data.animalData?.feedingFrequencies?.map((item: FeedingFrequencyData, index) => (
                      <th key={`${item.value}-${index}`}>{item.value}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.animalData?.feedingData?.map((feedingDataPoint: FeedingData, index) => (
                    <tr key={`${feedingDataPoint.lifeStage}-${index}`}>
                      <td>{feedingDataPoint.weight}</td>
                      <td>{feedingDataPoint.lifeStage}</td>
                      {data.animalData?.feedingFrequencies?.map((feedingFrequency: FeedingFrequencyData) => (
                        <td key={`${feedingFrequency.id}-${index}`}>
                          {getFeedingDetailString(feedingDataPoint.feedingDetails, feedingFrequency.id)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </StyledInfoBlock>
          )}
        </StyledInfoBlock>
      )}
      {data?.energyDeclaration && (
        <StyledInfoBlock data-testid="energyDeclaration">
          <Heading variant="h3" label>
            {t('energyDeclaration.heading')}
          </Heading>
          {data.energyDeclaration?.link && (
            <Text type="body">
              <Link href={data.energyDeclaration.link} data-testid="energyDeclaration_link" target="_blank">
                {t('energyDeclaration.linkText')}
              </Link>
            </Text>
          )}
          {data.energyDeclaration.image && (
            <StyledEnergyDeclarationImage
              alt={t('energyDeclaration.heading')}
              src={data.energyDeclaration.image}
              data-testid="energyDeclaration_image"
            />
          )}
        </StyledInfoBlock>
      )}
    </>
  );
};

export default ProductTableOfOther;
